import { QueryClient } from "@tanstack/react-query";
import { PortalRequestError } from "../api/client.js";
import { isNotAnError } from "../api/errors.js";

/**
 * Retry policy, expressed once in the defaults and overridden per-mutation.
 *
 *   GET      3 attempts, 300 / 900 / 2700ms with jitter, then a manual [ Retry ]
 *   writes   no automatic retry
 *   401      exactly one silent force-refreshed retry — handled IN THE CLIENT, below this
 *            layer, so every query inherits it
 *   429      never retried. It is a cooldown, and the control shows a live countdown
 *   403      never retried. It invalidates `me` instead — a stale-role signal
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 3) return false;
  if (!(error instanceof PortalRequestError)) return false;
  const f = error.failure;
  if (isNotAnError(f)) return false;
  if (f.kind === "ratelimit" || f.kind === "scope" || f.kind === "auth") return false;
  if (f.kind === "validation" || f.kind === "conflict" || f.kind === "notfound") return false;
  return f.retryable;
}

export function retryDelay(attempt: number): number {
  const base = 300 * 3 ** attempt; // 300, 900, 2700
  // Jitter so a fleet of tabs recovering from the same outage does not stampede.
  return base + Math.random() * 200;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay,
        refetchOnWindowFocus: true,
        // The portal never renders stale data as current without saying so, and it never
        // persists the cache to disk — a disk-cached invoice list on a shared machine is a
        // leak with no upside.
        gcTime: 5 * 60_000,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
